import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import styles from './Contact.module.css';
import Button from '../components/UI/Button';
import useForm from '../components/UI/hooks/useForm';

const Contact = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Replace these with your actual EmailJS credentials
    const EMAILJS_CONFIG = {
        SERVICE_ID: 'service_jeyrucd',       // Replace with your service ID
        TEMPLATE_ID: 'Contact_Form',     // Replace with your template ID
        PUBLIC_KEY: 'JrcxVFzAMPjwVqLB0'        // Replace with your public key
    };

    const { values, errors, isSubmitting, handleChange, handleSubmit } = useForm(
        {
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
        },
        validateForm
    );

    function validateForm(values) {
        const errors = {};
        if (!values.name) errors.name = 'Name is required';
        if (!values.email) {
        errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = 'Email is invalid';
        }
        if (!values.message) errors.message = 'Message is required';
        return errors;
    }

    const sendEmail = async (formValues) => {
        setIsLoading(true);
        setSubmitError(null);

        try {
        // Send email using EmailJS
        const result = await emailjs.send(
            EMAILJS_CONFIG.SERVICE_ID,
            EMAILJS_CONFIG.TEMPLATE_ID,
            {
            from_name: formValues.name,
            from_email: formValues.email,
            from_phone: formValues.phone,
            company: formValues.company,
            subject: formValues.subject,
            message: formValues.message,
            to_name: 'Apexbit Team', // Your company/recipient name
            reply_to: formValues.email
            },
            EMAILJS_CONFIG.PUBLIC_KEY
        );

        console.log('Email sent successfully:', result);
        setIsSubmitted(true);
        
        } catch (error) {
        console.error('Failed to send email:', error);
        setSubmitError('Failed to send message. Please try again or contact us directly at hello@apexbit.com');
        } finally {
        setIsLoading(false);
        }
    };

    const onSubmit = async (formValues) => {
        await sendEmail(formValues);
    };

    const contactInfo = [
        /*{
        icon: '📧',
        title: 'Email',
        value: 'hello@apexbit.com',
        link: 'mailto:hello@apexbit.com'
        }, 
        {
        icon: '📞',
        title: 'Phone',
        value: '(555) 123-4567',
        link: 'tel:+15551234567'
        },*/
        {
        icon: '📍',
        title: 'Address',
        value: 'Greater Manchester, UK',
        link: 'https://maps.google.com'
        },
        {
        icon: '🕒',
        title: 'Business Hours',
        value: 'Mon - Fri: 9AM - 5PM\nSat: 10AM - 2PM',
        link: null
        }
    ];

    if (isSubmitted) {
        return (
        <div className={styles.contactPage}>
            <section className={styles.hero}>
                <div className={styles.container}>
                    <h1>Thank You!</h1>
                    <p className={styles.subtitle}>
                        Your message has been sent successfully. We'll get back to you within 24 hours.
                    </p>
                    <Button as="a" href="/" variant="primary" size="large">
                        Return Home
                    </Button>
                </div>
            </section>
        </div>
        );
    }

    return (
        <div className={styles.contactPage}>
            {/* Hero Section */}
            <section className={styles.hero}>
                <div className={styles.container}>
                    <h1>Contact Us</h1>
                    <p className={styles.subtitle}>
                        Have a question or want to discuss a project? We'd love to hear from you.
                    </p>
                </div>
            </section>

            {/* Contact Content */}
            <div className={styles.container}>
                <div className={styles.contactContent}>
                    {/* Form Section */}
                    <section className={styles.formSection}>
                        <h2>Send us a Message</h2>
                        <form className={styles.contactForm} onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit(onSubmit);
                        }}>
                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                <label htmlFor="name">Full Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    className={errors.name ? styles.error : ''}
                                    disabled={isLoading}
                                />
                                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                                </div>
                                <div className={styles.formGroup}>
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    className={errors.email ? styles.error : ''}
                                    disabled={isLoading}
                                />
                                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                                </div>
                            </div>

                            <div className={styles.formRow}>
                                <div className={styles.formGroup}>
                                <label htmlFor="phone">Phone Number</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={values.phone}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                </div>
                                <div className={styles.formGroup}>
                                <label htmlFor="company">Company</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={values.company}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                />
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="subject">Subject</label>
                                <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={values.subject}
                                onChange={handleChange}
                                disabled={isLoading}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="message">Message *</label>
                                <textarea
                                id="message"
                                name="message"
                                rows="5"
                                value={values.message}
                                onChange={handleChange}
                                className={errors.message ? styles.error : ''}
                                disabled={isLoading}
                                />
                                {errors.message && <span className={styles.errorText}>{errors.message}</span>}
                            </div>

                            <Button
                                type="submit"
                                variant="primary"
                                size="large"
                                className={styles.submitButton}
                                loading={isLoading || isSubmitting}
                                disabled={isLoading || isSubmitting}
                            >
                                {isLoading ? 'Sending...' : 'Send Message'}
                            </Button>

                            {submitError && (
                                <div className={styles.errorMessage}>
                                {submitError}
                                </div>
                            )}
                        </form>
                    </section>

                    {/* Info Section */}
                    <section className={styles.infoSection}>
                        <h2>Get in Touch</h2>
                        
                        <div className={styles.contactInfo}>
                            {contactInfo.map((info, index) => (
                                <div key={index} className={styles.contactItem}>
                                    <div className={styles.contactIcon}>{info.icon}</div>
                                    <div className={styles.contactDetails}>
                                        <h3>{info.title}</h3>
                                        {info.link ? (
                                        <a href={info.link} className={styles.contactLink}>
                                            {info.value.split('\n').map((line, i) => (
                                            <span key={i}>{line}<br /></span>
                                            ))}
                                        </a>
                                        ) : (
                                        <p>
                                            {info.value.split('\n').map((line, i) => (
                                            <span key={i}>{line}<br /></span>
                                            ))}
                                        </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={styles.mapContainer}>
                            <h3>Our Location</h3>
                            <div className={styles.mapWrapper}>
                                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d303727.84061454586!2d-2.320019849999989!3d53.506450550000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487ba654d3882eb1%3A0x2cc7d82221c68081!2sGreater%20Manchester!5e0!3m2!1sen!2suk!4v1758029976904!5m2!1sen!2suk" 
                                width="100%" 
                                height="300" 
                                style= {{ border:0 }}
                                allowfullscreen="" 
                                loading="lazy" 
                                title="Apexbit Location - Greater Manchester"
                                referrerpolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                            <p className={styles.mapNote}>
                                Serving clients across Greater Manchester and beyond
                            </p>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Contact;
